(ns manage.adset-decision-tree-page.view.side-panel.poi.predicate-editor
  "POI-specific wrapper around the generic predicate editor. Looks up the parent
  POI node's open-status and derives the triggers it makes redundant, keeping the
  generic predicate-editor free of POI domain knowledge."
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/Button$default" :as Button]
            ["react-hook-form" :as rhf]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.view.context :as context]
            [manage.adset-decision-tree-page.view.predicate-editor.converter
             :refer [adjust-schema-to-property]]
            [manage.adset-decision-tree-page.view.predicate-editor.core
             :refer [predicate-editor-form]]
            [manage.adset-decision-tree-page.view.side-panel.poi.validation :as poi-validation]
            [manage.adset-decision-tree-page.view.side-panel.property-form
             :refer [get-property-field-name]]
            [uix.core :as uix :refer [$ defui defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(def ^:private poi-redundant-triggers
  (update-vals poi-validation/poi-redundant-triggers
               #(into #{} (map keyword) %)))

(defhook use-visible-schema-output
  "Hides schema-output triggers that the parent POI node's open-status already
  makes redundant, unless the user has explicitly configured them. Watches the
  configured triggers so hidden entries reappear once selected."
  [schema-output {:keys [control fields property]} redundant-triggers]
  (let [trigger-field-names
        (uix/use-memo
         #(to-array
           (if (seq redundant-triggers)
             (map-indexed
              (fn [i _] (get-property-field-name property (str i) :trigger))
              fields)
             []))
         [redundant-triggers fields property])
        configured-triggers (rhf/useWatch #js {:name trigger-field-names :control control})
        configured-trigger-set
        (uix/use-memo
         #(into #{} (keep (fn [v] (when v (keyword v)))) configured-triggers)
         [configured-triggers])]
    (uix/use-memo
     #(if (seq redundant-triggers)
        (reduce-kv (fn [acc k v]
                     (if (and (contains? redundant-triggers k)
                              (not (contains? configured-trigger-set k)))
                       acc
                       (assoc acc k v)))
                   {}
                   schema-output)
        schema-output)
     [redundant-triggers configured-trigger-set schema-output])))

(defui poi-predicate-editor
  [{:keys [selected-item form-control property field-array selected-node]}]
  (let [{:keys [control]} form-control
        {:keys [fields]} field-array
        parent-node (use-subscribe [::sub/node-data (:parent-id selected-node)])
        parent-open-status (get-in parent-node [:targeting :context 0 :param :open-status])
        redundant-triggers (get poi-redundant-triggers parent-open-status)
        schema (context/use-property-context property)
        schema-output (:output (adjust-schema-to-property property schema))
        visible-schema-output (use-visible-schema-output
                               schema-output
                               {:control control :fields fields :property property}
                               redundant-triggers)
        render-alert
        (uix/use-callback
         (fn [{:keys [trigger on-clear]}]
           (when (and trigger (contains? redundant-triggers trigger))
             ($ Alert {:severity "warning" :sx #js {:mt 1}
                       :action ($ Button {:size "small" :color "inherit" :onClick on-clear} "Clear")}
                (str "The strategy already limits to " parent-open-status " locations."))))
         [redundant-triggers parent-open-status])]
    ($ predicate-editor-form {:key [selected-item (:id property)]
                              :property property
                              :form-control form-control
                              :field-array field-array
                              :schema-output visible-schema-output
                              :render-alert render-alert})))
